import LineItem from "~/types/LineItem"
import BookingItem from "~/types/BookingItem"
import ModifierGroup from "~/types/ModifierGroup"

export type ModifierQuantities = Record<string, number>

export const modifierQuantitiesFromDetails = (
  modifierDetails: BookingItem["modifierDetails"] = []
) => {
  return (modifierDetails || []).reduce<ModifierQuantities>((memo, modifier) => {
    memo[modifier.id] = modifier.quantity
    return memo
  }, {})
}

export const modifierQuantitiesFromBookingItems = (bookingItems: BookingItem[]) => {
  return bookingItems.reduce<Record<string, ModifierQuantities>>((memo, bookingItem) => {
    if (!bookingItem.lineItemId) return memo

    memo[bookingItem.lineItemId] = modifierQuantitiesFromDetails(bookingItem.modifierDetails)
    return memo
  }, {})
}

export const compactModifiers = (modifiers: ModifierQuantities) => {
  return Object.entries(modifiers).reduce<ModifierQuantities>((memo, [modifierId, quantity]) => {
    if (quantity > 0) memo[modifierId] = quantity
    return memo
  }, {})
}

export const serializeModifiers = (modifiers?: ModifierQuantities) => {
  return Object.entries(modifiers || {})
    .sort(([leftId], [rightId]) => String(leftId).localeCompare(String(rightId)))
    .map(([id, quantity]) => ({
      id,
      quantity,
    }))
}

export const modifiersMatch = (
  currentModifiers: ModifierQuantities,
  existingModifiers: ModifierQuantities
) => {
  const currentIds = Object.keys(currentModifiers)
  const existingIds = Object.keys(existingModifiers)

  return (
    currentIds.length === existingIds.length &&
    currentIds.every((modifierId) => currentModifiers[modifierId] === existingModifiers[modifierId])
  )
}

export const bookingItemHasModifierChange = (
  existingBookingItem: BookingItem,
  currentModifiers: ModifierQuantities
) => {
  return !modifiersMatch(
    compactModifiers(currentModifiers),
    compactModifiers(modifierQuantitiesFromDetails(existingBookingItem.modifierDetails))
  )
}

export const modifierTotalCents = (
  lineItem: LineItem,
  lineItemQuantity: number,
  selectedModifiers: ModifierQuantities
) => {
  return (lineItem.modifierGroups || []).reduce((groupTotal, group) => {
    const multiplier = group.multiplyLimitsByQuantity ? 1 : lineItemQuantity

    return (
      groupTotal +
      group.modifiers.reduce((modifierTotal, modifier) => {
        const modifierQuantity = selectedModifiers[modifier.id] || 0
        return modifierTotal + modifier.priceCents * modifierQuantity * multiplier
      }, 0)
    )
  }, 0)
}

export const lineItemSubtotalCents = (
  lineItem: LineItem,
  lineItemQuantity: number,
  selectedModifiers: ModifierQuantities
) => {
  return (
    lineItem.amountCents * lineItemQuantity +
    modifierTotalCents(lineItem, lineItemQuantity, selectedModifiers)
  )
}

export const modifierPricingNote = (group: ModifierGroup) => {
  if (group.multiplyLimitsByQuantity) {
    return "Your choices are counted for the whole order. Example: quantity 2 with 6 choices per item lets you pick 12 total choices."
  }

  return "Your choices apply to each item. Example: quantity 2 with one extra selected adds that extra to both items, so the extra charge is counted twice."
}

export const modifierLimitLabel = (minAllowed: number, maxAllowed: number) => {
  if (minAllowed > 0 && maxAllowed > 0) {
    return minAllowed === maxAllowed ? `Choose ${minAllowed}` : `Choose ${minAllowed}-${maxAllowed}`
  }

  if (minAllowed > 0) return `Min: ${minAllowed}`
  if (maxAllowed > 0) return `Max: ${maxAllowed}`

  return null
}

export const modifierLimitsForQuantity = (group: ModifierGroup, lineItemQuantity: number) => {
  const baseMinAllowed = group.minAllowed || 0
  const baseMaxAllowed = group.maxAllowed || 0

  return {
    minAllowed: group.multiplyLimitsByQuantity ? baseMinAllowed * lineItemQuantity : baseMinAllowed,
    maxAllowed: group.multiplyLimitsByQuantity ? baseMaxAllowed * lineItemQuantity : baseMaxAllowed,
  }
}
